import React, { createContext, useContext, useState, useEffect } from 'react';
import translationsData from '../../_config/translations.json';
import config from '_config/config';
import { getCookie, setCookie } from 'utils/cookies';

const TranslationContext = createContext();

const TEXTS_COOKIE_PREFIX = 'viagens_texts_';

const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const deepMerge = (target, source) => {
    if (!isObject(target) || !isObject(source)) return source === undefined ? target : source;
    const merged = { ...target };
    Object.keys(source).forEach((key) => {
        merged[key] = isObject(source[key]) && isObject(target[key])
            ? deepMerge(target[key], source[key])
            : source[key];
    });
    return merged;
};

const resolveChannel = () =>
    localStorage.getItem('channel') || process.env.REACT_APP_TRAVEL_CHANNEL || 'safra';

const mergeCustomTexts = (rawTexts) => {
    const parsed = typeof rawTexts === 'string' ? JSON.parse(rawTexts) : rawTexts;
    return isObject(parsed) ? deepMerge(translationsData, parsed) : translationsData;
};

export const TranslationProvider = ({ children }) => {
    const [translations, setTranslations] = useState(() => {
        try {
            const cached = getCookie(`${TEXTS_COOKIE_PREFIX}${resolveChannel()}`);
            if (cached) return mergeCustomTexts(cached);
        } catch (e) { /* usa os defaults */ }
        return translationsData;
    });

    useEffect(() => {
        const channel = resolveChannel();
        const cookieName = `${TEXTS_COOKIE_PREFIX}${channel}`;
        if (getCookie(cookieName)) return;

        config.get(`whitelabels/byChannel/${channel}`)
            .then((response) => {
                const cfg = response?.data?.data?.config || {};
                const textsKey = Object.keys(cfg).find((k) => k === 'viagens_texts' || k.endsWith('_viagens_texts'));
                const customTexts = textsKey ? cfg[textsKey] : null;
                if (!customTexts) return;

                setTranslations(mergeCustomTexts(customTexts));
                setCookie(cookieName, typeof customTexts === 'string' ? customTexts : JSON.stringify(customTexts));
            })
            .catch(() => { /* mantém os defaults */ });
    }, []);

    const t = (keyPath, variables = {}) => {
        const keys = keyPath.split('.');
        let value = translations;

        for (const key of keys) {
            if (value && value[key] !== undefined) {
                value = value[key];
            } else {
                console.warn(`Translation key not found: ${keyPath}`);
                return keyPath;
            }
        }

        if (typeof value === 'string') {
            let formattedValue = value;
            Object.keys(variables).forEach(variable => {
                formattedValue = formattedValue.replace(`{${variable}}`, variables[variable]);
            });
            return formattedValue;
        }

        return value;
    };

    return (
        <TranslationContext.Provider value={{ t }}>
            {children}
        </TranslationContext.Provider>
    );
};

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }
    return context;
};
